<template>
  <div class="overlay-loading" v-show="isWaitingForServer"></div>
  <div v-if="isWaitingForServer" class="loading-indicator">
    <div class="spinner"></div>
    <span>{{ loadingMessage }}</span>
  </div>
  <div v-if="errorMessage" class="error-message">
    <span>{{ errorMessage }}</span>
    <button class="button-small" @click="errorMessage = ''">✕</button>
  </div>
  <div class="env-select">
    <h2>Choose environment</h2>
    <p class="env-help">Select an API environment before logging in.</p>
    <ul class="env-list">
      <li v-for="env in environments" :key="env.name">
        <button class="button env-button" @click.stop="selectEnvironment(env)">
          <span class="env-name">{{ env.name }}</span>
          <span class="env-url">{{ env.url }}</span>
        </button>
        <div v-if="isSelected(env)" class="user-list">
          <div v-if="isLocalDev(env)" class="port-config">
            <label for="localPort">Port:</label>
            <input 
              type="number" 
              id="localPort" 
              v-model.number="localDevPort" 
              @input="updateLocalDevUrl(env)"
              min="1" 
              max="65535"
            />
            <span class="port-info">Current URL: {{ getEffectiveUrl(env) }}</span>
          </div>
          <p class="user-title">Log in as</p>
          <ul class="user-buttons">
            <li v-for="user in getUsers(env)" :key="user.username">
              <button class="button" @click.stop="loginUser(env, user)" :disabled="isLoggingIn">
                {{ user.username }}
              </button>
            </li>
          </ul>
          <p v-if="!getUsers(env).length" class="env-empty">No users configured.</p>
        </div>
      </li>
    </ul>
    <p v-if="!environments.length" class="env-empty">No environments configured.</p>
  </div>
</template>

<script>
import store from '../store.js';
import { login } from '../api.js';

export default {
  name: 'EnvironmentSelect',
  data() {
    return {
      store,
      environments: [],
      isWaitingForServer: false,
      isLoggingIn: false,
      errorMessage: '',
      loadingMessage: 'Loading...',
      localDevPort: 57802
    };
  },
  mounted() {
    this.environments = this.loadEnvironments();
    store.environments = this.environments;
    if (!this.environments.length) {
      this.errorMessage = 'No environments configured in VUE_APP_ENVIRONMENTS. Check your .env file.';
    } else if (this.environments.length === 1 && !store.environment) {
      // Auto-select if only one environment
      this.selectEnvironment(this.environments[0]);
    }
  },
  methods: {
    loadEnvironments() {
      try {
        const envList = process.env.VUE_APP_ENVIRONMENTS || '[]';
        const parsed = JSON.parse(envList);
        if (!Array.isArray(parsed)) {
          this.errorMessage = 'VUE_APP_ENVIRONMENTS must be an array of environments.';
          return [];
        }
        return parsed;
      } catch (e) {
        this.errorMessage = `Failed to parse VUE_APP_ENVIRONMENTS: ${e.message}`;
        return [];
      }
    },
    selectEnvironment(env) {
      store.environment = env;
      if (this.isLocalDev(env)) {
        // Extract default port from URL on first selection
        if (!this.localDevPort || this.localDevPort === 57802) {
          const match = env.url.match(/:(\d+)/);
          if (match) this.localDevPort = parseInt(match[1]);
        }
        store.apiBaseUrl = this.getEffectiveUrl(env);
      } else {
        store.apiBaseUrl = env.url;
      }
      store.users = Array.isArray(env.users) ? env.users : [];
      store.page = 'env';
    },
    isSelected(env) {
      return store.environment && store.environment.name === env.name;
    },
    getUsers(env) {
      return Array.isArray(env.users) ? env.users : [];
    },
    isLocalDev(env) {
      return env.name === 'Local Dev' || env.url.includes('localhost');
    },
    getEffectiveUrl(env) {
      if (this.isLocalDev(env)) {
        return `https://localhost:${this.localDevPort}`;
      }
      return env.url;
    },
    updateLocalDevUrl(env) {
      if (this.isLocalDev(env) && this.isSelected(env)) {
        store.apiBaseUrl = this.getEffectiveUrl(env);
      }
    },
    loginUser(env, user) {
      this.errorMessage = '';
      this.isWaitingForServer = true;
      this.isLoggingIn = true;
      this.loadingMessage = 'Logging in...';
      this.selectEnvironment(env);
      login(user.username, user.password).then(() => {
        this.isWaitingForServer = false;
        this.isLoggingIn = false;
      }).catch((err) => {
        this.isWaitingForServer = false;
        this.isLoggingIn = false;
        this.errorMessage = `Login failed: ${err?.response?.data?.error_description || err?.message || 'Unknown error'}`;
      });
    }
  }
};
</script>

<style scoped>
.env-select {
  max-width: 640px;
}
.env-help {
  margin: 0 0 10px 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  margin: 0 0 10px 0;
}
button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.env-list > li {
  margin: 0 0 15px 0;
}
.user-list {
  margin: 8px 0 0 0;
  padding: 0 0 0 8px;
  border-left: 3px solid var(--color-green);
}
.port-config {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(155, 208, 176, 0.1);
  border-radius: 4px;
}
.port-config label {
  font-weight: 600;
  font-size: 0.9em;
}
.port-config input[type="number"] {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid var(--color-green);
  border-radius: 4px;
}
.port-info {
  font-size: 0.85em;
  color: var(--color-blue);
  margin-left: 8px;
}
.user-title {
  margin: 0 0 6px 0;
  font-weight: 600;
}
.user-buttons li {
  display: inline-block;
  margin: 0 8px 8px 0;
}
.env-button {
  min-width: 320px;
}
.env-name {
  font-weight: 700;
}
.env-url {
  font-size: 0.85em;
  opacity: 0.9;
}
.env-empty {
  color: var(--color-disabled);
}
.error-message {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #d32f2f;
  color: white;
  padding: 10px 14px;
  border-radius: 6px;
  z-index: 1002;
  max-width: 400px;
}
.error-message button {
  background: transparent;
  border: 1px solid white;
  color: white;
  padding: 2px 8px;
  margin: 0;
  cursor: pointer;
  font-size: 14px;
  min-width: auto;
}
.loading-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--color-blue);
  color: var(--color-text-light);
  padding: 8px 12px;
  border-radius: 6px;
  z-index: 1001;
}
.spinner {
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
